/* eslint-disable no-undef */
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import qs from 'qs';
import useUser from 'hooks/useUser';
import { useOrder } from './OrderContext';
import { useToken } from 'hooks/useToken';

const AuthContext = createContext();

const deleteAllCookies = () => {
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
};

// Function to enhance user with fullname and initials
const enhanceUser = (user) => {
  if (user) {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim(); // Handle missing names
    const initials = `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase(); // Get initials

    return {
      ...user,
      fullName,
      initials,
      getInitials: function () {
        return this.initials;
      }
    };
  }
  return user;
};

export const AuthProvider = ({ children }) => {
  const { getUser, setUser } = useUser();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const clearAuthState = () => {
    console.log("clearAuthState called");
    setUser(null);
    setRoles([]);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
    deleteAllCookies();
  };

  const continueSignup = async (token) => {
    try {
      setToken(token);
      localStorage.setItem('token', token);
      await fetchUser();
      return true;
    } catch (error) {
      console.error('Error during login:', error);
      clearAuthState();
      return false;
    }
  };

  const login = async (credentials) => {
    try {
      // Clear existing token to prevent conflicts
      setToken(null);
      localStorage.removeItem('token');

      const data = qs.stringify({
        'app_login': credentials.username,
        'app_password': credentials.password,
        'method': 'GET'
      });

      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BACKEND_URL}/module/logon/getToken?${data}`,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      };

      const response = await axios.request(config);
      if (response.data.token) {
        setToken(response.data.token);
        localStorage.setItem('token', response.data.token);
        await fetchUser();
        return true;
      } else {
        clearAuthState();
        return false;
      }
    } catch (error) {
      console.error('Error during login:', error);
      clearAuthState();
      return false;
    }
  };

  const loginWithToken = async (token, user) => {
    try {
      // Load search history like addresses
      try {
        const historyResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/module/history/getSearchHistory?integrationId=${user.integrationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (historyResponse.data.status === 'success') {
          user.searchHistory = historyResponse.data.data || [];
        } else {
          user.searchHistory = [];
        }
      } catch (historyError) {
        console.error('Error fetching search history:', historyError);
        user.searchHistory = [];
      }

      setUser(enhanceUser(user));
      setRoles([user.userRole]);
      setToken(token);
      localStorage.setItem('token', token);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('user', JSON.stringify(user));
      
      return true;
    } catch (error) {
      console.error('Error during login with token:', error);
      clearAuthState();
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loginWithFacebook = () => {
    return new Promise((resolve, reject) => {
      try {
        if (!window.FB) {
          reject(new Error('Facebook SDK not loaded'));
          return;
        }

        window.FB.login(function (response) {
          if (response.authResponse) {
            window.FB.api('/me', 'GET', { fields: 'id,name,email' }, async function (userResponse) {
              try {
                if (userResponse && !userResponse.error) {
                  const { email } = userResponse;
                  const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/module/user/facebookAuthCallback`, { email });

                  if (response.data) {
                    await loginWithToken(response.data.token, response.data);
                    resolve(response.data);
                  } else {
                    reject(new Error('Must sign up first'));
                  }
                } else {
                  reject(new Error(userResponse.error || 'Error fetching user data'));
                }
              } catch (error) {
                reject(error);
              }
            });
          } else {
            reject(new Error('User cancelled login or did not fully authorize'));
          }
        }, { scope: 'public_profile,email' });
      } catch (error) {
        reject(error);
      }
    });
  };

  const logout = useCallback(async () => {
    try {
      await axios.get(`${process.env.REACT_APP_BACKEND_URL}/logout.php`);
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      clearAuthState();
      // Force a page reload to ensure all old state is cleared
      window.location = "/"
      // window.location.reload();
    }
  }, []);

  const fetchUser = async () => {
    try {
      const token = useToken();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/module/user/fetchUser`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.status === 'success') {
        const userData = response.data.data;
          try {
          const addressResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/module/address/getAddress`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (addressResponse.data.status === 'success') {
            const addresses = Array.isArray(addressResponse.data.data) ? addressResponse.data.data : (addressResponse.data || []);
            userData.addresses = addresses;
            userData.activeAddress = addresses.find(addr => addr.isActive) || addresses[0] || null;
          } else {
            userData.addresses = [];
            userData.activeAddress = null;
          }
        } catch (addressError) {
          console.error('Error fetching addresses:', addressError);
          userData.addresses = [];
          userData.activeAddress = null;
        }
        
        // Load search history like addresses
        try {
          const historyResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/module/history/getSearchHistory?integrationId=${userData.integrationId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (historyResponse.data.status === 'success') {
            userData.searchHistory = historyResponse.data.data || [];
          } else {
            userData.searchHistory = [];
          }
        } catch (historyError) {
          console.error('Error fetching search history:', historyError);
          userData.searchHistory = [];
        }
        
        // Fetch collections
        try {
          const collectionsResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/module/bookmarkcollection/getAllCollections`, 
            { userId: userData.id },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          if (collectionsResponse.data.status === 'success') {
            userData.collections = collectionsResponse.data.data || [];
          } else {
            userData.collections = [];
          }
        } catch (collectionsError) {
          console.error('Error fetching collections:', collectionsError);
          userData.collections = [];
        }
        
        setUser(enhanceUser(userData));
        setRoles([userData.userRole]);
      } else {
        console.log("HNEEEEEEEEEE");
        clearAuthState();
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      clearAuthState();
    } finally {
      setLoading(false);
    }
  };

  const hasToken = useCallback(() => {
    return !!localStorage.getItem('token');
  }, []);

  const refreshUserData = async () => {
    if (token) {
      await fetchUser();
    }
  };

  useEffect(() => {
    if (token) {
      const savedUser = getUser();
      if (savedUser) {
        setRoles([savedUser.userRole]);
        setLoading(false);
      } else {
        const id = localStorage.getItem('userId');
        if (id) {
          fetchUser();
        } else {
          setLoading(false);
        }
      }
    } else {
      setLoading(false);
    }
  }, [token, getUser]);

  const updateUserAddresses = useCallback(async () => {
    const currentUser = getUser();
    if (currentUser?.integrationId && token) {
      try {
        const addressResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/module/address/getAddress`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (addressResponse.data.status === 'success') {
          const updatedUser = {
            ...currentUser,
            addresses: addressResponse.data.data || [],
            activeAddress: (addressResponse.data.data || []).find(addr => addr.isActive) || (addressResponse.data.data || [])[0] || null
          };
          setUser(updatedUser);
        }
      } catch (error) {
        console.error('Error updating addresses:', error);
      }
    }
  }, [getUser, token]);

  const setActiveAddress = useCallback(async (addressId) => {
    const currentUser = getUser();
    if (currentUser?.addresses) {
      const selectedAddress = currentUser.addresses.find(addr => addr.integrationId === addressId);
      if (selectedAddress) {
        const updatedUser = {
          ...currentUser,
          activeAddress: selectedAddress
        };
        setUser(updatedUser);
          try {
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/module/address/setActiveAddress`, 
            { integrationId: addressId },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
        } catch (error) {
          console.error('Error setting active address:', error);
        }
      }
    }
  }, [getUser, token]);

  const saveSearchHistory = useCallback(async (textSearch) => {
    const currentUser = getUser();
    if (currentUser?.integrationId && token && textSearch?.trim()) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/module/history/saveSearchHistory`, 
          { 
            textSearch: textSearch.trim(),
            integrationId: currentUser.integrationId 
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const newItem = {
          integrationId: response.data.data?.historyId || Date.now().toString(),
          textSearch: textSearch.trim(),
          dateTime: new Date().toISOString()
        };
        const updatedUser = {
          ...currentUser,
          searchHistory: [newItem, ...(currentUser.searchHistory || [])]
        };
        setUser(updatedUser);
      } catch (error) {
        console.error('Error saving search history:', error);
      }
    }
  }, [getUser, token]);

  const deleteSearchHistory = useCallback(async (historyId) => {
    const currentUser = getUser();
    if (currentUser?.integrationId && token && historyId) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/module/history/deleteSearchHistory`, 
          { integrationId: historyId },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.data.status === 'success') {
          const updatedUser = {
            ...currentUser,
            searchHistory: (currentUser.searchHistory || []).filter(item => item.integrationId !== historyId)
          };
          setUser(updatedUser);
        }
      } catch (error) {
        console.error('Error deleting search history:', error);
      }
    }
  }, [getUser, token]);

  const clearSearchHistory = useCallback(async () => {
    const currentUser = getUser();
    if (currentUser?.integrationId && token) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/module/history/clearSearchHistory`, 
          { integrationId: currentUser.integrationId },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.data.status === 'success') {
          const updatedUser = {
            ...currentUser,
            searchHistory: []
          };
          setUser(updatedUser);
        }
      } catch (error) {
        console.error('Error clearing search history:', error);
      }
    }
  }, [getUser, token]);

  return (
    <AuthContext.Provider value={{ 
      user: getUser(), 
      setUser, 
      roles, 
      login, 
      loginWithToken, 
      continueSignup, 
      loginWithFacebook, 
      logout, 
      loading, 
      hasToken, 
      refreshUserData,
      updateUserAddresses,
      setActiveAddress,
      searchHistory: getUser()?.searchHistory || [],
      saveSearchHistory,
      deleteSearchHistory,
      clearSearchHistory
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);